import React from "react";
import styled from "styled-components";
import PhoneFrame from "../components/PhoneFrame";
import StoreButtons from "../components/StoreButtons";
import budgetScreenshot from "../assets/screenshots/budget.webp";
import qrAppStore from "../assets/svg/qr-appstore.svg";

const Section = styled.section`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 6.5rem 2rem 2rem;

  @media screen and (max-width: 48em) {
    padding: 5.5rem 1rem 1.5rem;
  }
`;

// One big rounded card holding the whole hero, the phone is cropped by its bottom edge
const Card = styled.div`
  width: 100%;
  max-width: 1280px;
  border-radius: 48px;
  background: var(--paper-2);
  overflow: hidden;
  display: grid;
  grid-template-columns: 1.1fr 0.9fr;
  align-items: stretch;

  @media screen and (max-width: 48em) {
    border-radius: 32px;
    display: flex;
    flex-direction: column;
  }
`;

const TextSide = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: flex-start;
  padding: 5rem 2rem 5rem 5rem;

  @media screen and (max-width: 64em) {
    padding: 4rem 1.5rem 4rem 3rem;
  }

  @media screen and (max-width: 48em) {
    align-items: center;
    text-align: center;
    padding: 3rem 1.5rem 2.5rem;
  }
`;

const Offer = styled.p`
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.375rem 0.875rem;
  margin-bottom: 1.5rem;
  border-radius: var(--radius-full);
  background: rgba(21, 184, 127, 0.14);
  color: #0a7a53;
  font-family: var(--fontR);
  font-size: 0.8125rem;
  font-weight: 500;

  &::before {
    content: "";
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--green);
  }
`;

const Title = styled.h1`
  font-family: var(--fontL);
  font-weight: 500;
  font-size: clamp(2.5rem, 4.6vw, 3.75rem);
  line-height: 1.08;
  letter-spacing: -0.03em;
  color: var(--text-hi);
  margin-bottom: 1.25rem;
  text-wrap: balance;
`;

const Subtitle = styled.p`
  font-family: var(--fontR);
  font-size: 1.0625rem;
  line-height: 1.6;
  color: var(--text-lo);
  max-width: 31rem;
  margin-bottom: 2rem;
`;

const Reassurance = styled.ul`
  list-style: none;
  display: flex;
  flex-wrap: wrap;
  gap: 0.375rem 1.25rem;
  margin-top: 1.5rem;
  font-family: var(--fontR);
  font-size: 0.8125rem;
  color: var(--text-lo);

  li {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
  }

  li::before {
    content: "✓";
    color: var(--green);
    font-weight: 600;
  }

  @media screen and (max-width: 48em) {
    justify-content: center;
  }
`;

const VisualSide = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  align-items: flex-start;
  padding-top: 3.5rem;
  /* the frame is taller than the card on purpose: the bottom is cropped */
  max-height: 600px;

  @media screen and (max-width: 48em) {
    padding-top: 0;
    max-height: 420px;
  }
`;

// Desktop visitors cannot install an iOS app from their computer: send them to their phone
const QrCard = styled.div`
  display: none;

  @media screen and (min-width: 64em) {
    position: absolute;
    right: 2.5rem;
    bottom: 2.5rem;
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.5rem;
    padding: 0.75rem 0.75rem 0.625rem;
    border-radius: 16px;
    background: #ffffff;
    box-shadow: 0 16px 40px rgba(10, 10, 11, 0.16);
  }

  img {
    width: 96px;
    height: 96px;
    display: block;
  }

  p {
    font-family: var(--fontR);
    font-size: 0.6875rem;
    font-weight: 500;
    color: var(--text-hi);
    text-align: center;
    line-height: 1.3;
  }
`;

const Hero = () => (
  <Section id="hero">
    <Card>
      <TextSide>
        <Offer>Offre de lancement : 100 % gratuit</Offer>
        <Title>
          Ton argent. Mieux.
          <br />
          Simplement.
        </Title>
        <Subtitle>
          Nea connecte tes comptes, classe tes dépenses et te dit ce qu'il te reste vraiment. Et quand tu te demandes
          quoi faire de ton épargne, tu lui poses simplement la question.
        </Subtitle>
        <StoreButtons tone="light" position="hero" />
        <Reassurance>
          <li>Connexion bancaire sécurisée</li>
          <li>Lecture seule</li>
          <li>Données jamais revendues</li>
        </Reassurance>
      </TextSide>

      <VisualSide>
        <PhoneFrame
          src={budgetScreenshot}
          alt="Écran Budget de l'app nea : ce qu'il te reste ce mois-ci"
          width="min(310px, 72vw)"
          priority
        />
        <QrCard>
          <img src={qrAppStore} alt="QR code vers nea sur l'App Store" width="96" height="96" />
          <p>
            Scanne pour
            <br />
            installer l'app
          </p>
        </QrCard>
      </VisualSide>
    </Card>
  </Section>
);

export default Hero;
